import dayjs from 'dayjs';

// The absolute scope for the Pages-to-Review endpoints (`/pages`,
// `/pages/counts`). Unlike the day-granular KPI/chart endpoints, these pin the
// table to an exact, whole-second UTC window and never take the calendar-date
// form. `dateTimeTo` is inclusive of the second it names.
export type DiscoverabilityPagesScope = {
  dateTimeFrom: string;
  dateTimeTo: string;
  crawlers?: string[];
  domains?: string[];
};

// Inputs to resolve a Pages-to-Review scope. Either the scan-scope window
// (both `scanDateTimeFrom` and `scanDateTimeTo`, forwarded verbatim) takes
// precedence, or the calendar picker days are resolved to local instants.
export type DiscoverabilityPagesScopeInput = {
  dateFrom: string;
  dateTo: string;
  crawlers?: string[];
  domain?: string;
  scanDateTimeFrom?: string;
  scanDateTimeTo?: string;
  scanDomains?: string[];
};

// The endpoints reject millisecond precision (`.000Z` → 400), so emit a
// whole-second ISO-8601 instant with an explicit UTC offset by dropping the
// millisecond fraction `Date.prototype.toISOString` always appends.
export function toSecondPrecisionUtc(instant: Date): string {
  return instant.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

// fetchBaseQuery's URLSearchParams default joins arrays with commas, but the
// REST API's class-validator DTO expects list params as repeated query params
// (`crawlers=a&crawlers=b`, `domains=a&domains=b`). Serialize the scope
// explicitly so each list value becomes its own param. An empty list is
// omitted entirely — never write `domains=`, which the endpoint would 400.
export function appendPagesScopeParams(
  search: URLSearchParams,
  scope: DiscoverabilityPagesScope
): void {
  search.append('dateTimeFrom', scope.dateTimeFrom);
  search.append('dateTimeTo', scope.dateTimeTo);
  if (scope.crawlers) {
    scope.crawlers.forEach((crawler) => search.append('crawlers', crawler));
  }
  if (scope.domains) {
    scope.domains.forEach((domain) => search.append('domains', domain));
  }
}

// Resolves the Pages-to-Review scope from the URL state. In scan scope (the
// banner's Inspect-pages window, carrying both absolute bounds) the window and
// evaluable domains pass through unchanged. Otherwise the calendar picker days
// resolve to absolute instants in the BROWSER's local timezone — start of the
// from-day and the inclusive 23:59:59 of the to-day — preserving the same day
// boundaries the user sees in the picker (previously resolved server-side from
// `dateFrom`/`dateTo`+`timezone`). A single-domain filter becomes a one-entry
// `domains` list.
export function resolveDiscoverabilityPagesScope(
  input: DiscoverabilityPagesScopeInput
): DiscoverabilityPagesScope {
  const inScanScope =
    input.scanDateTimeFrom !== undefined && input.scanDateTimeTo !== undefined;

  if (inScanScope) {
    return {
      dateTimeFrom: input.scanDateTimeFrom as string,
      dateTimeTo: input.scanDateTimeTo as string,
      crawlers: input.crawlers,
      domains: input.scanDomains,
    };
  }

  return {
    dateTimeFrom: toSecondPrecisionUtc(dayjs(input.dateFrom).startOf('day').toDate()),
    dateTimeTo: toSecondPrecisionUtc(dayjs(input.dateTo).endOf('day').toDate()),
    crawlers: input.crawlers,
    domains: input.domain !== undefined ? [input.domain] : undefined,
  };
}
